import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

/** Cabinet oblique projection */
function proj(x: number, y: number, z: number, ox = 0, oy = 0): [number, number] {
  return [ox + x - y * 0.4, oy - z + y * 0.35];
}

function boxPoly(cx: number, cy: number, cz: number, sx = 22, sy = 18, sz = 18, ox = 0, oy = 0) {
  const corners = [
    proj(cx,    cy,    cz,    ox, oy),
    proj(cx+sx, cy,    cz,    ox, oy),
    proj(cx+sx, cy+sy, cz,    ox, oy),
    proj(cx,    cy+sy, cz,    ox, oy),
    proj(cx,    cy,    cz+sz, ox, oy),
    proj(cx+sx, cy,    cz+sz, ox, oy),
    proj(cx+sx, cy+sy, cz+sz, ox, oy),
    proj(cx,    cy+sy, cz+sz, ox, oy),
  ];
  return corners;
}

function faceStr(corners: [number, number][], indices: number[]) {
  return indices.map(i => `${corners[i][0].toFixed(1)},${corners[i][1].toFixed(1)}`).join(' ');
}

export function TranslateDiagram({ values }: DiagramProps) {
  const v = values.v ?? [10, 0, 0];
  const vx = v[0] ?? 0;
  const vy = v[1] ?? 0;
  const vz = v[2] ?? 0;

  const ox = 70, oy = 100;
  const maxOff = Math.max(Math.abs(vx), Math.abs(vy), Math.abs(vz), 1);
  const arrowScale = Math.min(90, maxOff * 4) / maxOff;

  const srcCorners = boxPoly(0, 0, 0, 22, 18, 18, ox, oy);
  const dstCorners = boxPoly(
    vx * arrowScale * 0.5, vy * arrowScale * 0.5, vz * arrowScale * 0.5,
    22, 18, 18, ox, oy
  );

  const srcCenter = proj(11, 9, 9, ox, oy);
  const dstCenter = proj(
    11 + vx * arrowScale * 0.5,
    9  + vy * arrowScale * 0.5,
    9  + vz * arrowScale * 0.5,
    ox, oy
  );

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Translate diagram">
      <defs>
        <marker id="arr-t" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0,0.5 L6,3.5 L0,6.5 Z" fill="#f59e0b" />
        </marker>
        {/* Axis gizmo arrows */}
        <marker id="ax-x" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.X_COLOR} />
        </marker>
        <marker id="ax-y" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.Y_COLOR} />
        </marker>
        <marker id="ax-z" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Ghost box (source) */}
      <polygon points={faceStr(srcCorners, [0,1,5,4])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />
      <polygon points={faceStr(srcCorners, [1,2,6,5])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />
      <polygon points={faceStr(srcCorners, [4,5,6,7])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />

      {/* Translation arrow */}
      <line x1={srcCenter[0]} y1={srcCenter[1]}
            x2={dstCenter[0]} y2={dstCenter[1]}
            stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arr-t)" />

      {/* Solid box (destination) */}
      <polygon points={faceStr(dstCorners, [0,1,2,3])}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <polygon points={faceStr(dstCorners, [0,1,5,4])}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={faceStr(dstCorners, [1,2,6,5])}
               fill="rgba(96,165,250,0.04)" stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={faceStr(dstCorners, [4,5,6,7])}
               fill="rgba(96,165,250,0.10)" stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Translation vector label */}
      <text x={(srcCenter[0] + dstCenter[0]) / 2 + 4}
            y={(srcCenter[1] + dstCenter[1]) / 2 - 8}
            fontSize="10" fontFamily="monospace" fill="#f59e0b" opacity="0.95" textAnchor="middle">
        [{vx}, {vy}, {vz}]
      </text>

      {/* Axis gizmo (bottom right) */}
      {[
        { label: 'X', color: D.X_COLOR, dx: 18, dy: 0,  markerId: 'ax-x' },
        { label: 'Y', color: D.Y_COLOR, dx: -7, dy: 6,  markerId: 'ax-y' },
        { label: 'Z', color: D.Z_COLOR, dx: 0,  dy: -18, markerId: 'ax-z' },
      ].map(({ label, color, dx, dy, markerId }) => (
        <g key={label}>
          <line x1={235} y1={120} x2={235 + dx} y2={120 + dy}
                stroke={color} strokeWidth="1.5" markerEnd={`url(#${markerId})`} />
          <text x={235 + dx * 1.5} y={120 + dy * 1.5 + 3}
                fontSize="9" fontFamily="monospace" fill={color} textAnchor="middle">{label}</text>
        </g>
      ))}
    </svg>
  );
}
